'use client';

import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { createSessionRecheck } from './session-recheck';

interface HeimdallSessionProviderProps {
  baseURL?: string;
  children: ReactNode;
}

/**
 * One per app: the provider holds the Heimdall client and refetches its session when an RTK
 * request answers 401. The store prepends `sessionRecheckMiddleware`.
 */
export function createHeimdallSession<
  Client extends { useSession: () => { refetch: () => unknown } },
>(createClient: (baseURL: string) => Client) {
  type Session = ReturnType<Client['useSession']>;

  const { middleware, useSessionRecheck } = createSessionRecheck();
  // The session rides along with the client: without a provider there is no client whose
  // useSession the hooks could call, and calling it only sometimes would break hook order.
  const Context = createContext<{ client: Client; session: Session } | null>(null);

  function HeimdallSessionReader({ baseURL, children }: Required<HeimdallSessionProviderProps>) {
    const client = useMemo(() => createClient(baseURL), [baseURL]);
    const session = client.useSession() as Session;
    const value = useMemo(() => ({ client, session }), [client, session]);

    useSessionRecheck(async () => {
      await session.refetch();
    });

    return <Context.Provider value={value}>{children}</Context.Provider>;
  }

  /** Static pages, such as the error pages, render without Heimdall's URL and so without a session. */
  function HeimdallSessionProvider({ baseURL, children }: HeimdallSessionProviderProps) {
    if (!baseURL) {
      return children;
    }

    return <HeimdallSessionReader baseURL={baseURL}>{children}</HeimdallSessionReader>;
  }

  // Both hooks give null on pages rendered without Heimdall's URL.
  return {
    HeimdallSessionProvider,
    sessionRecheckMiddleware: middleware,
    useHeimdallClient: () => useContext(Context)?.client ?? null,
    useHeimdallSession: () => useContext(Context)?.session ?? null,
  };
}
